import { lineCount } from './text';
import { toolActivitySummary, toolGroupState } from './tool-activity';
import { browserToolTitle, toolDetail } from './tool-identity';
import type { DynamicToolPart, TerminalPart, ToolActivityPart } from './types';

export type ToolCallGroupModel = {
  diffTotals: { additions: number; deletions: number };
  hasDiff: boolean;
  progressTitle: string;
  state: DynamicToolPart['state'];
  summary: ReturnType<typeof toolActivitySummary>;
  terminalsByToolCallId: Map<string, TerminalPart>;
  visibleParts: readonly ToolActivityPart[];
};

/**
 * Derive everything the grouped tool-call card needs from a run of consecutive
 * tool-activity parts: the aggregated diff counts, the per-tool-call terminal
 * lookup, the terminals folded into their owning tool (so they are not rendered
 * twice), the collapsed group state, and the header/progress title.
 */
export function toolCallGroupModel(parts: readonly ToolActivityPart[]): ToolCallGroupModel {
  const tools = parts.filter((part): part is DynamicToolPart => part.type === 'dynamic-tool');
  const diffTotals = parts.reduce(
    (totals, part) => {
      if (part.type !== 'data-diff') return totals;
      totals.additions += lineCount(part.data.newText);
      totals.deletions += lineCount(part.data.oldText);
      return totals;
    },
    { additions: 0, deletions: 0 },
  );
  const hasDiff = diffTotals.additions > 0 || diffTotals.deletions > 0;
  const toolCallIds = new Set(tools.map((part) => part.toolCallId));
  const terminalsByToolCallId = new Map(
    parts
      .filter((part): part is TerminalPart => part.type === 'data-terminal')
      .map((part) => [part.data.toolCallId, part] as const),
  );
  const visibleParts = parts.filter(
    (part) => part.type !== 'data-terminal' || !toolCallIds.has(part.data.toolCallId),
  );
  const state = toolGroupState(tools);
  const activeTool = tools.findLast(
    (part) => part.state === 'input-streaming' || part.state === 'input-available',
  );
  const activeToolTitle =
    activeTool === undefined
      ? null
      : toolDetail(
          activeTool,
          browserToolTitle(activeTool.toolName, activeTool.title, activeTool.input),
        );
  const summary = toolActivitySummary(parts);
  const progressTitle = activeToolTitle === null ? summary.details : activeToolTitle;

  return {
    diffTotals,
    hasDiff,
    progressTitle,
    state,
    summary,
    terminalsByToolCallId,
    visibleParts,
  };
}
